import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { CaptchaWidget } from "../../../components/CaptchaWidget/CaptchaWidget";
import EmailCodeStep from "../../../components/EmailCodeStep/EmailCodeStep";
import { COMPANY_REQUEST_URL, COMPANY_REQUEST_EMAIL_CODE_URL } from "../../../features/UrlHelper";

/**
 * Formular ziadosti o prevadzku -- spolocny pre DEMO aj ZAUJEM.
 *
 * Dva kroky (ochrana pred robotmi):
 *   1. formular + Turnstile -> server posle na e-mail 6-miestny kod a vrati
 *      podpisany listok (POST /api/company-requests/email-code)
 *   2. zaujemca zada kod -> az teraz sa ziadost ulozi (POST /api/company-requests)
 * Po ulozeni len oznami, ze ziadost caka na schvalenie; pristupy pridu
 * mailom az ked ju SUPERADMIN v /sprava schvali.
 */

// Zhodne so slugify v backend/services/tenantRegistry.js -- navrh adresy
// z nazvu, aby zaujemca videl, ako bude vyzerat.
export function navrhSlugu(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 35)
    .replace(/-+$/g, "");
}

const PRAZDNY = {
  nazov_prevadzky: "",
  adresa_prevadzky: "",
  kontaktna_osoba: "",
  email: "",
  telefon: "",
  pozadovany_slug: "",
  poznamka: "",
};

const NADPIS = {
  DEMO: "Vyskúšajte si Rozvozy na vlastnej demo prevádzke",
  ZAUJEM: "Chcem Rozvozy pre svoju prevádzku",
};

function CompanyRequestForm({ typ, onTypChange, onOdoslane }) {
  const [form, setForm] = useState(PRAZDNY);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [krok, setKrok] = useState("form"); // form | kod | done
  const [odosiela, setOdosiela] = useState(false);
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState("");
  const [listok, setListok] = useState(null);
  // Kod, ktory server prijal, ale ziadost odmietol z ineho dovodu (napr.
  // obsadena adresa). Po oprave vo formulari sa pouzije znova -- zaujemca
  // nemusi cakat na novy mail ani znova riesit captchu.
  const [overeny, setOvereny] = useState(null); // { email, listok, kod }
  const turnstileRef = useRef(null);

  const pole = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  // Pri pisani rovno upravime na tvar, ktory adresa smie mat -- zaujemca tak
  // nenarazi na chybu "len male pismena bez diakritiky". Pomlcku na konci
  // nechavame (pise sa dalsie slovo), odstrani ju az navrhSlugu pri odoslani.
  const zmenAdresu = (e) => {
    const hodnota = e.target.value
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-+/, "");
    setForm((prev) => ({ ...prev, pozadovany_slug: hodnota }));
  };

  const predpona = typ === "DEMO" ? "demo-" : "";
  const slug = navrhSlugu(form.pozadovany_slug) || navrhSlugu(form.nazov_prevadzky);
  const nahladAdresy = slug ? `rozvozy.sk/${predpona}${slug}` : null;
  const email = form.email.trim().toLowerCase();

  const spravaZoServera = (err, predvolena) => err?.response?.data?.message || predvolena;

  const vycisti = () => {
    setChyba("");
    setInfo("");
  };

  // krok 2 -- ulozenie ziadosti s kodom
  const ulozZiadost = async (pouzityListok, pouzityKod) => {
    setOdosiela(true);
    vycisti();
    try {
      await axios.post(COMPANY_REQUEST_URL, {
        ...form,
        typ,
        // aj nezmeneny navrh z nazvu -- zaujemca ho videl ako "Vaša adresa bude",
        // tak ho ma dostat (a server hned overi, ci nie je obsadeny)
        pozadovany_slug: slug || null,
        emailListok: pouzityListok,
        emailKod: pouzityKod,
      });
      setKrok("done");
      onOdoslane?.();
    } catch (err) {
      if (err?.response?.data?.kod === "EMAIL_CODE") {
        // zly alebo vyprsany kod -- ostava sa pri kode
        setOvereny(null);
        setKrok("kod");
      } else {
        // kod bol v poriadku, vadi nieco vo formulari (napr. obsadena adresa)
        setOvereny({ email, listok: pouzityListok, kod: pouzityKod });
        setKrok("form");
      }
      setChyba(spravaZoServera(err, "Žiadosť sa nepodarilo odoslať. Skúste to znova."));
    } finally {
      setOdosiela(false);
    }
  };

  // krok 1 -- formular: poslat kod na e-mail
  const odosliFormular = async (e) => {
    e.preventDefault();
    vycisti();
    if (overeny && overeny.email === email) {
      await ulozZiadost(overeny.listok, overeny.kod);
      return;
    }
    if (!captchaToken) {
      setChyba("Prosím, potvrďte, že nie ste robot.");
      return;
    }
    setOdosiela(true);
    try {
      const { data } = await axios.post(COMPANY_REQUEST_EMAIL_CODE_URL, {
        typ,
        email,
        cfTurnstileToken: captchaToken,
      });
      setListok(data.listok);
      setKrok("kod");
    } catch (err) {
      setChyba(spravaZoServera(err, "Overovací kód sa nepodarilo odoslať. Skúste to znova."));
    } finally {
      setOdosiela(false);
      // Turnstile token je jednorazovy -- server ho uz pouzil
      turnstileRef.current?.reset?.();
      setCaptchaToken(null);
    }
  };

  // Znovuposlanie bez novej captchy -- server prijme predosly listok pre ten isty e-mail.
  const posliZnova = async () => {
    vycisti();
    setOdosiela(true);
    try {
      const { data } = await axios.post(COMPANY_REQUEST_EMAIL_CODE_URL, { typ, email, predoslyListok: listok });
      setListok(data.listok);
      setInfo("Poslali sme vám nový kód. Platí len ten najnovší.");
    } catch (err) {
      setChyba(spravaZoServera(err, "Kód sa nepodarilo poslať znova."));
    } finally {
      setOdosiela(false);
    }
  };

  if (krok === "done") {
    return (
      <section className="landing__form landing__form--done" aria-live="polite">
        <CheckCircle2 size={40} aria-hidden="true" />
        <h2>Ďakujeme, žiadosť sme prijali</h2>
        <p>
          Žiadosť teraz čaká na schválenie. Prístupové údaje vám pošleme na <strong>{form.email}</strong>.
        </p>
      </section>
    );
  }

  if (krok === "kod") {
    return (
      <section className="landing__form landing__form--kod">
        {/* Ten isty krok pouziva registracia zakaznika, obnova hesla aj zmena
            e-mailu v profile (R7) -- odpocet, texty o spame ani sestmiestne
            pole sa preto nekopiruju sem. */}
        <EmailCodeStep
          email={email}
          onSubmit={(zadany) => ulozZiadost(listok, zadany)}
          onResend={posliZnova}
          onBack={() => { vycisti(); setKrok("form"); }}
          submitLabel="Overiť a odoslať žiadosť"
          backLabel="Upraviť údaje alebo e-mail"
          popis={
            <>
              Na <strong>{email}</strong> sme poslali 6-miestny overovací kód. Zadajte ho sem —
              až potom žiadosť odošleme. Kód platí 15 minút.
            </>
          }
          chyba={chyba}
          info={info}
          odosiela={odosiela}
        />
      </section>
    );
  }

  return (
    <section className="landing__form">
      <div className="landing__form-tabs" role="tablist">
        {["DEMO", "ZAUJEM"].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={typ === t}
            className={`landing__form-tab${typ === t ? " landing__form-tab--active" : ""}`}
            onClick={() => onTypChange(t)}
          >
            {t === "DEMO" ? "Chcem vyskúšať (DEMO)" : "Mám záujem"}
          </button>
        ))}
      </div>

      <h2 className="landing__form-title">{NADPIS[typ]}</h2>

      <form onSubmit={odosliFormular} className="landing__form-grid">
        <label className="landing__field">
          <span>Názov prevádzky *</span>
          <input required maxLength={50} {...pole("nazov_prevadzky")} placeholder="Hostinec U Jána" />
        </label>
        <label className="landing__field">
          <span>Adresa prevádzky <small className="landing__hint">(ulica, mesto)</small></span>
          <input maxLength={100} {...pole("adresa_prevadzky")} placeholder="Hlavná 1, Košice" />
        </label>
        <label className="landing__field">
          <span>Kontaktná osoba *</span>
          <input required maxLength={100} {...pole("kontaktna_osoba")} placeholder="Meno a priezvisko" />
        </label>
        <label className="landing__field">
          <span>E-mail *</span>
          <input required type="email" maxLength={50} {...pole("email")} placeholder="vy@prevadzka.sk" />
        </label>
        <label className="landing__field">
          <span>Telefón</span>
          <input type="tel" maxLength={20} {...pole("telefon")} placeholder="+421 900 000 000" />
        </label>
        <div className="landing__field landing__field--wide">
          <label htmlFor="pozadovany-slug">
            Webová adresa vašej prevádzky <small className="landing__hint">(nepovinné)</small>
          </label>
          <div className="landing__url">
            <span className="landing__url-prefix" aria-hidden="true">rozvozy.sk/{predpona}</span>
            <input
              id="pozadovany-slug"
              maxLength={35}
              value={form.pozadovany_slug}
              onChange={zmenAdresu}
              placeholder={navrhSlugu(form.nazov_prevadzky) || "hostinec-u-jana"}
              aria-describedby="pozadovany-slug-popis"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <small id="pozadovany-slug-popis" className="landing__hint">
            Na tejto adrese vaši zákazníci uvidia denné menu a vy aj váš tím sa tu budete prihlasovať.
            Ak pole necháte prázdne, adresu odvodíme z názvu prevádzky.
            {typ === "DEMO" && " Demo prevádzka má na začiatku adresy „demo-“."}
          </small>
          {nahladAdresy && (
            <p className="landing__url-preview">
              Vaša adresa bude: <strong>{nahladAdresy}</strong>
            </p>
          )}
        </div>
        <label className="landing__field landing__field--wide">
          <span>Poznámka</span>
          <textarea rows={3} maxLength={2000} {...pole("poznamka")} />
        </label>

        <div className="landing__field--wide">
          <CaptchaWidget onVerify={setCaptchaToken} turnstileRef={turnstileRef} />
        </div>

        {chyba && <p className="landing__error landing__field--wide" role="alert">{chyba}</p>}

        <p className="landing__hint landing__field--wide">
          Na zadaný e-mail vám pošleme overovací kód. Žiadosť odošleme až po jeho zadaní.
        </p>

        <button type="submit" className="landing__btn landing__btn--primary landing__field--wide" disabled={odosiela}>
          {odosiela ? "Odosielam..." : "Pokračovať"}
        </button>
      </form>
    </section>
  );
}

export default CompanyRequestForm;
